/**
 * WaterSurface — the ceiling (SPEC §10.4). Four summed Gerstner waves, shaded from BOTH
 * sides because the camera crosses the waterline: run shallow and the rig lifts above the
 * surface on its own, which is the shot where the dorsal fin cuts the water.
 *
 * Foam is driven by the player: a bow cap at the fin plus a tapering wake behind it, keyed
 * to how far the shark is out of the water.
 */
import * as THREE from 'three';
import { OCEAN, WATER } from '../data/tuning.ts';
import { TOKENS, hexToLinear } from '../voxel/Palette.ts';
import { applyEnv, type SkyEnv } from '../world/DayNight.ts';

/**
 * direction.xy, wavelength, amplitude scale — incommensurate so the sum never tiles.
 *
 * Shallow-water wavelets, not ocean swell: short, closely spaced, and spread across a wide
 * range of headings so they interfere into a restless mottle instead of marching as ranks.
 * Two longer members carry a slow underlying set; the rest is wind chop on top.
 */
const WAVES: Array<[number, number, number, number]> = [
  [1.0, 0.26, 6.4, 1.0],
  [-0.55, 0.84, 4.3, 0.74],
  [0.38, -0.93, 2.9, 0.52],
  [-0.91, -0.42, 2.0, 0.36],
];

/**
 * Unrolled at build time rather than looped: the wave set is fixed, and a `#define` with
 * backslash continuations does not survive a JS template literal intact (the literal eats
 * the escaped newlines).
 */
function waveGlsl(): string {
  const n = WAVES.length;
  return WAVES.map(([dx, dz, len, amp], i) => {
    const d = Math.hypot(dx, dz) || 1;
    const nx = (dx / d).toFixed(4);
    const nz = (dz / d).toFixed(4);
    // A little per-member speed jitter, or six wavelets locked to one celerity beat as a
    // single rigid pattern.
    const spd = (1.0 + i * 0.06).toFixed(3);
    return `  {
    vec2 d = vec2(${nx}, ${nz});
    float k = 6.2831853 / ${len.toFixed(3)};
    float a = uAmp * ${amp.toFixed(4)};
    float phi = k * dot(d, p) - uTime * ${spd} * uWaveSpeed * k;
    float c = cos(phi), s = sin(phi);
    float q = min(uSteep / (k * a * ${n}.0 + 1e-4), 1.0);
    disp.xz += q * a * d * c;
    disp.y  += a * s;
    nAcc.xz -= d * (k * a * c);
    nAcc.y  -= q * k * a * s;
    peak += a * s;
    total += a;
  }`;
  }).join('\n');
}

const VERT = /* glsl */ `
uniform float uTime;
uniform float uAmp;
uniform float uSteep;
uniform float uWaveSpeed;

varying vec3  vWorld;
varying vec3  vNormal;
varying float vCrest;
varying float vDepth;

void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  vec2 p = world.xz;

  vec3 disp = vec3(0.0);
  vec3 nAcc = vec3(0.0, 1.0, 0.0);
  float peak = 0.0;
  float total = 0.0;

  // Gerstner: crests pinch and troughs broaden as steepness rises, which is the whole
  // reason to use it over a sum of sines — a sine ocean reads as corrugated iron.
${waveGlsl()}

  world.xyz += disp;

  vec4 mv = viewMatrix * world;
  vWorld = world.xyz;
  vNormal = normalize(nAcc);
  vCrest = clamp(peak / max(total, 1e-4) * 0.5 + 0.5, 0.0, 1.0);
  vDepth = -mv.z;
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = /* glsl */ `
uniform float uTime;
uniform vec3  uShallow;
uniform vec3  uDeep;
uniform vec3  uSunColor;
uniform vec3  uSunDir;
uniform vec3  uFoamColor;
uniform vec3  uFogColor;
uniform float uFogDensity;
uniform float uSurfaceY;
uniform vec2  uFoamPos;
uniform vec2  uFoamDir;
uniform float uFoamStrength;

varying vec3  vWorld;
varying vec3  vNormal;
varying float vCrest;
varying float vDepth;

float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash12(i),                 hash12(i + vec2(1.0, 0.0)), u.x),
             mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), u.x), u.y);
}

// Bow cap at the fin plus a wake that widens and fades astern. Worked in the shark's own
// frame so the wake always trails the heading rather than a world axis.
float finFoam(vec2 p) {
  if (uFoamStrength <= 0.001) return 0.0;
  vec2 d = p - uFoamPos;
  vec2 fwd = normalize(uFoamDir);
  vec2 side = vec2(fwd.y, -fwd.x);
  float along = dot(d, fwd);
  float across = dot(d, side);

  // Tight cap of churn at the fin itself.
  float bow = exp(-dot(d, d) * 0.75);

  // Astern only. The length term clamps to 0 ahead of the fin, so anything using it has to
  // be gated or it evaluates to full strength forward of the shark.
  float behind = max(-along, 0.0);

  // Kelvin wake: two diverging crest lines at ~21 degrees off the track, forming the V.
  // This is the shape that reads as "something is cutting the surface" — a straight trail
  // behind the fin reads as a scratch on the water instead.
  //
  /* But the arms are not RULED lines. A real wake's diverging crests are a train of feathered
     cusps that wanders with the water carrying it, and the wander grows astern: near the fin
     the wake is tight and coherent, far back it has had time to disperse. That growth is what
     makes it read as a wake ageing rather than as a wavy line drawn on the sea.

     The two arms are given opposite phase through the side sign, so the V never pulses
     symmetrically — a symmetric wobble is the tell that both arms are one analytic curve. */
  /* THE ASTERN GATE. Note: no backticks anywhere in this file's GLSL — it lives inside a JS
     template literal and one would end the string.

     'behind' clamps to 0 ahead of the fin, so any term built on it sits at FULL strength all
     the way forward along the track unless multiplied by this. All three terms below need it.
     The wedge term was missing it, and it went unnoticed only because armX used to be exactly
     0 at the fin, making the strip infinitely thin. Giving the arms a wobble made armX
     non-zero there, and the latent bug became a white line shooting out of the shark's nose —
     visible wherever there was open water ahead to see it against. */
  float astern = smoothstep(0.0, 1.4, behind);

  float sideSign = across < 0.0 ? -1.0 : 1.0;
  float wob = sin(behind * 0.62 - uTime * 1.7 + sideSign * 2.1) * 0.55
            + sin(behind * 1.45 + uTime * 1.1 + sideSign * 0.8) * 0.26
            + (vnoise(vec2(behind * 0.30, uTime * 0.22 + sideSign * 5.0)) - 0.5) * 1.5;

  float armX = max(0.0, behind * 0.40 + wob * (0.18 + behind * 0.055) * astern);
  // The crest broadens as it spreads, and frays unevenly while it does.
  float armW = (0.32 + behind * 0.055) * (0.82 + 0.36 * vnoise(vec2(behind * 0.5, sideSign * 9.0 + uTime * 0.3)));
  float arm = exp(-pow(abs(across) - armX, 2.0) / (armW * armW));
  arm *= exp(-behind * 0.075) * astern;

  // Faint churn filling the wedge between the arms so the V encloses disturbed water
  // rather than reading as two loose lines.
  float inside = step(abs(across), armX) * exp(-behind * 0.13) * astern * 0.22;

  float f = bow * 1.6 + arm * 1.6 + inside;
  // Break the analytic shape up — but gently, or the grain eats the V lines themselves.
  float grain = vnoise(p * 1.9 - vec2(0.0, uTime * 0.6)) * 0.55
              + vnoise(p * 4.6 + vec2(uTime * 0.4, 0.0)) * 0.45;
  f *= 0.62 + 0.62 * grain;
  return clamp(f, 0.0, 1.0) * uFoamStrength;
}

/* Capillary ripple, normal-only. The geometric wavelets are deliberately low, so without
   this the surface reads as glass; perturbing the normal at a scale far below what the mesh
   could resolve is what puts the fine restless shimmer back at no vertex cost. Gradients are
   analytic, so it stays stable however close the camera gets. */
vec3 microNormal(vec2 p, float t) {
  const vec2 d1 = vec2(0.8700, 0.4930);
  const vec2 d2 = vec2(-0.3600, 0.9330);
  const vec2 d3 = vec2(0.5200, -0.8540);
  float k1 = 4.3, k2 = 6.9, k3 = 11.3;
  float a1 = 0.0115, a2 = 0.0072, a3 = 0.0038;
  float f1 = dot(p, d1) * k1 - t * 1.55;
  float f2 = dot(p, d2) * k2 + t * 1.15;
  float f3 = dot(p, d3) * k3 - t * 2.30;
  vec2 grad = d1 * (a1 * k1 * cos(f1)) + d2 * (a2 * k2 * cos(f2)) + d3 * (a3 * k3 * cos(f3));
  return normalize(vec3(-grad.x, 1.0, -grad.y));
}

void main() {
  vec3 N = normalize(vNormal);
  vec3 V = normalize(cameraPosition - vWorld);
  // Above the waterline the camera sees the outside of the surface; below it sees the
  // underside. One shader, one blend factor, so crossing the line is continuous.
  float above = clamp((cameraPosition.y - uSurfaceY) / 1.5, 0.0, 1.0);
  vec3 Nf = above > 0.5 ? N : -N;

  // Fold the capillary ripple in before anything is shaded with it.
  vec3 micro = microNormal(vWorld.xz, uTime);
  Nf = normalize(vec3(Nf.x + micro.x, Nf.y, Nf.z + micro.z));

  float fres = pow(1.0 - clamp(dot(Nf, V), 0.0, 1.0), 4.0);

  // Sun glint. Much broader than an open-ocean specular: a shallow rippled surface throws
  // a field of small highlights that reads as sparkle, where a tight exponent gives one
  // hard sun spot and leaves the rest of the surface dead.
  vec3 H = normalize(uSunDir + V);
  float glint = pow(max(dot(Nf, H), 0.0), 42.0);

  float foam = clamp(finFoam(vWorld.xz) + smoothstep(0.80, 0.99, vCrest) * 0.55, 0.0, 1.0);

  vec3 col;
  float alpha;
  if (above > 0.5) {
    // From outside: mostly reflected sky, going opaque at grazing angles.
    col = mix(uShallow, uSunColor, fres * 0.55);
    col += uSunColor * glint * 1.15;
    alpha = mix(0.16, 0.88, fres);
  } else {
    // From beneath: Snell's window — the sky compressed into a bright cone overhead, the
    // rest of the surface mirroring the dark water back down.
    float window = smoothstep(0.62, 0.98, dot(Nf, V));
    col = mix(uDeep, uShallow, window);
    col += uSunColor * window * 0.5;
    col += uSunColor * glint * 0.45;
    alpha = mix(0.55, 0.10, window);
  }

  // Foam both replaces the surface colour and adds light: churned water scatters far more
  // than it transmits, so it has to out-brighten the lit sand showing through from below.
  col = mix(col, uFoamColor, foam);
  col += uFoamColor * foam * 0.45;
  alpha = mix(alpha, 0.97, foam);

  float fd = vDepth * uFogDensity;
  float fogAmt = 1.0 - exp(-fd * fd);
  col = mix(col, uFogColor, fogAmt);
  alpha *= 1.0 - fogAmt * 0.85;

  gl_FragColor = vec4(col, alpha);

  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export class WaterSurface {
  readonly mesh: THREE.Mesh;

  private readonly geometry: THREE.PlaneGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly foamPos = new THREE.Vector2();
  private readonly foamDir = new THREE.Vector2(0, 1);

  /** world size of one quad; the plane snaps to this so vertices never swim through the field */
  private readonly quad: number;

  /**
   * Smaller and denser than an ocean plane, and it rides with the player. The wave field is
   * evaluated in world XZ, so moving the plane does not move the water — provided it snaps
   * to a whole number of quads, every vertex lands back on the same phase it had before.
   * A 640 u plane at 2.9 u quads could not resolve a 2 u wavelet at all.
   */
  constructor(size = 200, segments = 200) {
    this.quad = size / segments;
    this.geometry = new THREE.PlaneGeometry(size, size, segments, segments);
    this.geometry.rotateX(-Math.PI / 2);
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), size);

    this.material = new THREE.ShaderMaterial({
      // Named so a compile failure says WHICH shader; three logs the material name and
      // nothing else identifying, and an empty name means reading every shader in the project.
      name: 'WaterSurface',
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uTime: { value: 0 },
        uAmp: { value: OCEAN.gerstnerAmplitude },
        uSteep: { value: OCEAN.gerstnerSteepness },
        uWaveSpeed: { value: OCEAN.waveSpeed },
        uShallow: { value: new THREE.Vector3(...hexToLinear(0xbfe8e4)) },
        uDeep: { value: new THREE.Vector3(...hexToLinear(TOKENS.hydro)) },
        uSunColor: { value: new THREE.Vector3(...hexToLinear(WATER.sunColor)) },
        uSunDir: { value: new THREE.Vector3(...WATER.sunDir).normalize() },
        uFoamColor: { value: new THREE.Vector3(...hexToLinear(0xf2fbfa)) },
        uFogColor: { value: new THREE.Vector3(...hexToLinear(WATER.fogColor)) },
        uFogDensity: { value: WATER.fogDensity },
        uSurfaceY: { value: OCEAN.surfaceHeight },
        uFoamPos: { value: this.foamPos },
        uFoamDir: { value: this.foamDir },
        uFoamStrength: { value: 0 },
      },
      transparent: true,
      // Both faces matter, and the surface must never punch a hole in the depth buffer —
      // the shark is often directly beneath it.
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: true,
      fog: false,
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.name = 'water-surface';
    this.mesh.position.y = OCEAN.surfaceHeight;
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 4;
  }

  /**
   * @param breach 0 fully submerged, 1 fully clear — Player.breach
   * @param heading radians; the wake trails behind this
   */
  update(time: number, x: number, z: number, heading: number, breach: number): void {
    // Snapped to the quad grid: an unsnapped follow would slide the vertices through the
    // wave field and the whole surface would visibly crawl as you swim.
    this.mesh.position.x = Math.round(x / this.quad) * this.quad;
    this.mesh.position.z = Math.round(z / this.quad) * this.quad;
    this.foamPos.set(x, z);
    this.foamDir.set(Math.sin(heading), Math.cos(heading));
    this.material.uniforms.uTime.value = time;
    this.material.uniforms.uFoamStrength.value = breach;
  }


  /** the day cycle drives this material's lighting; see world/DayNight.ts */
  setEnv(env: SkyEnv): void {
    applyEnv(this.material.uniforms, env);
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}
